// src/data/trainingData.js
export const eligibleDegrees = ['BCA', 'B.Tech', 'MCA', 'M.Tech', 'B.Sc IT'];

export const trainingPrograms = [
  {
    id: 1,
    slug: 'web-development-training',
    category: 'Development',
    icon: 'Code2',
    title: 'Web Development Training & Internship',
    tagline: 'Build and ship real, production-style web applications.',
    description:
      "A hands-on training and internship track where students go from HTML/CSS fundamentals to building full-stack web applications using a modern frontend framework (React or Angular — your choice) and an ASP.NET Core (C#) backend, the same way our engineering team builds production software.",
    scope:
      "Web development covers everything needed to build and ship a website or web application: structuring and styling pages, programming interactivity in the browser, building the server-side logic and database that power an app, and deploying it so real users can reach it. Trained web developers work as frontend developers, backend developers, full-stack developers, or web application engineers — roles present in almost every software team, from startups to enterprises.",
    trackOptions: ['React', 'Angular'],
    trackLabel: 'Frontend track',
    trainingGuideUrl: '/training-material/web-development-training-guide.md',
    trainingGuidePdfUrl: '/training-material/web-development-training-guide.pdf',
    durations: ['6 Weeks', '6 Months'],
    roadmap: {
      sixWeeks: [
        {
          period: 'Week 1',
          title: 'HTML5 & CSS3 Foundations',
          scope: 'Structure and style web pages the way production frontends are actually built — semantic markup, the box model, and responsive layout.',
          stack: ['HTML5', 'CSS3', 'Flexbox', 'CSS Grid'],
          topics: [
            'Semantic HTML5 elements and document structure',
            'The CSS box model, specificity, and cascade',
            'Flexbox for one-dimensional layouts',
            'CSS Grid for two-dimensional layouts',
            'Responsive design with media queries and mobile-first CSS'
          ]
        },
        {
          period: 'Week 2',
          title: 'JavaScript Fundamentals (ES6+)',
          scope: 'Learn the language every frontend and backend JavaScript framework is built on.',
          stack: ['JavaScript (ES6+)'],
          topics: [
            'Variables, scope, and data types (let/const, primitives, objects)',
            'Functions, arrow functions, and closures',
            'Array methods: map, filter, reduce, find',
            'Destructuring, spread/rest, and template literals',
            'ES modules: import/export'
          ]
        },
        {
          period: 'Week 3',
          title: 'DOM, Events & Asynchronous JavaScript',
          scope: 'Make pages interactive and talk to real APIs — the bridge between static pages and dynamic apps.',
          stack: ['DOM APIs', 'Fetch API', 'JSON'],
          topics: [
            'DOM selection, traversal, and manipulation',
            'Event handling and event delegation',
            'Callbacks, Promises, and async/await',
            'Fetching and consuming REST APIs with fetch',
            'Working with JSON data'
          ]
        },
        {
          period: 'Week 4',
          title: 'Frontend Framework Fundamentals',
          scope: 'Build UIs the way production frontend teams do — as composable, stateful components. Pick your track: React or Angular.',
          choice: true,
          variants: {
            React: {
              stack: ['React', 'JSX'],
              topics: [
                'JSX syntax and component composition',
                'Props vs. state',
                'The useState and useEffect hooks',
                'Conditional rendering and rendering lists',
                'Component lifecycle and re-renders'
              ]
            },
            Angular: {
              stack: ['Angular', 'TypeScript'],
              topics: [
                'Components, modules, and TypeScript basics',
                'Property binding, event binding, and two-way binding',
                'Directives: *ngIf, *ngFor, and custom directives',
                'Services and dependency injection',
                'Component lifecycle hooks'
              ]
            }
          }
        },
        {
          period: 'Week 5',
          title: 'Routing, Forms, API Integration & Git',
          scope: 'Turn single components into a real multi-page application connected to a backend, tracked in version control.',
          choice: true,
          variants: {
            React: {
              stack: ['React Router', 'Git', 'GitHub', 'REST APIs'],
              topics: [
                'Client-side routing with react-router-dom',
                'Controlled forms and validation',
                'Calling REST APIs from React and handling loading/error states',
                'Git fundamentals: commits, branches, merges',
                'Collaborating with GitHub: pull requests and code review'
              ]
            },
            Angular: {
              stack: ['Angular Router', 'RxJS', 'Git', 'GitHub'],
              topics: [
                'Client-side routing with the Angular Router',
                'Reactive forms and validation',
                'Calling REST APIs with HttpClient and RxJS Observables',
                'Git fundamentals: commits, branches, merges',
                'Collaborating with GitHub: pull requests and code review'
              ]
            }
          }
        },
        {
          period: 'Week 6',
          title: 'Capstone: Build & Deploy',
          scope: 'Plan, build, and ship a complete single-page application end to end, the way it would happen on a real team.',
          stack: ['React or Angular', 'REST APIs', 'Netlify/Vercel/Azure Static Web Apps'],
          topics: [
            'Scoping a small application from a brief',
            'Building the app across the week with daily mentor check-ins',
            'Deploying to a live URL',
            'Demo day: presenting the finished project'
          ]
        }
      ],
      sixMonths: [
        {
          period: 'Month 1',
          title: 'Frontend Foundations',
          scope: 'Same foundation as the 6-week track (HTML/CSS/JavaScript), covered at a deeper pace with more exercises and code reviews.',
          stack: ['HTML5', 'CSS3', 'JavaScript (ES6+)'],
          topics: [
            'HTML5, CSS3, Flexbox & Grid, responsive design',
            'JavaScript fundamentals, ES6+ syntax, array methods',
            'DOM manipulation and event handling',
            'Weekly mini-projects with mentor code review'
          ]
        },
        {
          period: 'Month 2',
          title: 'Frontend Framework & Engineering',
          scope: 'Go from framework basics to building production-style frontends with routing, forms, and real APIs. Pick your track: React or Angular.',
          choice: true,
          variants: {
            React: {
              stack: ['React', 'React Router', 'Git/GitHub'],
              topics: [
                'React components, hooks, and state management patterns',
                'React Router and multi-page application structure',
                'Forms, validation, and API integration',
                'Git/GitHub workflow used on the internship’s live project'
              ]
            },
            Angular: {
              stack: ['Angular', 'Angular Router', 'RxJS', 'Git/GitHub'],
              topics: [
                'Angular components, services, and dependency injection',
                'Angular Router and multi-page application structure',
                'Reactive forms, validation, and API integration with HttpClient',
                'Git/GitHub workflow used on the internship’s live project'
              ]
            }
          }
        },
        {
          period: 'Month 3',
          title: 'Backend Development with ASP.NET Core',
          scope: 'Learn to build the server side of an application: APIs, business logic, and request handling, on the .NET stack our engineering team ships in production.',
          stack: ['ASP.NET Core', 'C#', 'REST API Design', 'Entity Framework Core'],
          topics: [
            'C# fundamentals and the .NET project structure',
            'Building REST APIs with ASP.NET Core Web API: controllers, routing, model binding',
            'Dependency injection and middleware',
            'Data access with Entity Framework Core',
            'Request validation and error handling'
          ]
        },
        {
          period: 'Month 4',
          title: 'Databases, Auth & Full-Stack Integration',
          scope: 'Persist data and secure an application, then connect the frontend and backend into one working product.',
          stack: ['SQL Server', 'Entity Framework Core', 'ASP.NET Core Identity', 'JWT Authentication'],
          topics: [
            'Relational database design and SQL Server fundamentals',
            'Modeling data with Entity Framework Core migrations',
            'Authentication and authorization with ASP.NET Core Identity and JWTs',
            'Wiring the React/Angular frontend to a real ASP.NET Core API'
          ]
        },
        {
          period: 'Month 5',
          title: 'Deployment, Testing & Cloud Basics',
          scope: 'Take an application from a developer’s laptop to a reliable, live, monitored service.',
          stack: ['Docker', 'xUnit', 'CI/CD', 'Azure'],
          topics: [
            'Environment configuration and secrets management',
            'Unit testing an ASP.NET Core API with xUnit',
            'Containerizing an ASP.NET Core app with Docker',
            'CI/CD pipeline basics and deploying to Azure App Service'
          ]
        },
        {
          period: 'Month 6',
          title: 'Live Project & Capstone',
          scope: 'Build a complete full-stack application end to end under mentor review, ready to present in interviews.',
          stack: ['React or Angular', 'ASP.NET Core', 'SQL Server', 'Azure'],
          topics: [
            'Project planning and architecture design',
            'Full-stack build across the month with weekly mentor reviews',
            'Deployment to a live, production-style environment',
            'Final demo day and portfolio/resume preparation'
          ]
        }
      ]
    },
    curriculum: {
      sixWeeks: [
        'HTML5, CSS3, and responsive layout fundamentals',
        'Modern JavaScript (ES6+): DOM, async/await, fetch',
        'Frontend framework fundamentals — React or Angular (your choice)',
        'REST API integration and form handling',
        'Version control with Git & GitHub',
        'Mini capstone: a deployed single-page application'
      ],
      sixMonths: [
        'Everything in the 6-week track',
        'Backend development with ASP.NET Core (C#) and REST API design',
        'Database design and data access with SQL Server & Entity Framework Core',
        'Authentication with ASP.NET Core Identity and JWTs',
        'Deployment: CI/CD basics, Azure cloud hosting, environment config',
        'Live project: a full-stack application built and deployed under mentor review'
      ]
    },
    outcomes: [
      'Build and deploy a full-stack web application end to end',
      'Work confidently with Git, REST APIs, and relational databases',
      'Understand production coding standards and code review practices',
      'A project you can showcase in interviews and on your resume'
    ],
    tools: ['HTML/CSS', 'JavaScript', 'React', 'Angular', 'ASP.NET Core', 'C#', 'SQL Server', 'Git'],
    seo: {
      title: 'Web Development Training & Internship (React/Angular/.NET) for Students | EDSY Tech',
      description:
        'Industrial web development training & internship for BCA, B.Tech, MCA, M.Tech and B.Sc IT students. 6 weeks or 6 months, React or Angular frontend, ASP.NET Core backend. Apply now.',
      keywords:
        'web development training, web development internship, .NET training for students, ASP.NET Core training, React training for students, Angular training for students, 6 weeks industrial training, 6 months internship, full stack .NET training'
    },
    featured: true
  },
  {
    id: 2,
    slug: 'mobile-app-development-training',
    category: 'Development',
    icon: 'Smartphone',
    title: 'Mobile App Development Training & Internship',
    tagline: 'Design and build Android/iOS-ready apps from a single codebase.',
    description:
      "Learn to design, build, and ship cross-platform mobile applications using React Native or Flutter (your choice), backed by real REST APIs, Firebase services, and app-store-ready practices.",
    scope:
      "Mobile development covers designing and building apps that run on Android and iOS phones and tablets — UI/UX suited to touch and small screens, navigation between screens, talking to backend APIs, storing data on-device, and packaging an app for the Play Store and App Store. Trained mobile developers work as mobile app developers, cross-platform engineers, or full-stack engineers who also ship the mobile client.",
    trainingGuideUrl: '/training-material/mobile-app-development-training-guide.md',
    trainingGuidePdfUrl: '/training-material/mobile-app-development-training-guide.pdf',
    trackOptions: ['React Native', 'Flutter'],
    trackLabel: 'Framework track',
    durations: ['6 Weeks', '6 Months'],
    roadmap: {
      sixWeeks: [
        {
          period: 'Week 1',
          title: 'Mobile UI/UX Fundamentals & Platform Guidelines',
          scope: 'Learn to design for touch, small screens, and two different platform conventions before writing any app code.',
          stack: ['Android (Material Design)', 'iOS (Human Interface Guidelines)', 'Figma'],
          topics: [
            'Mobile-first UI/UX principles: touch targets, thumb zones, gestures',
            'Android Material Design vs. iOS Human Interface Guidelines',
            'Wireframing a mobile screen flow',
            'Choosing between native, cross-platform, and hybrid approaches'
          ]
        },
        {
          period: 'Week 2',
          title: 'Cross-Platform Framework Fundamentals',
          scope: 'Build your first screens and components in your chosen framework.',
          choice: true,
          variants: {
            'React Native': {
              stack: ['React Native', 'JavaScript/TypeScript', 'Expo'],
              topics: [
                'Setting up a project with Expo',
                'Core components: View, Text, Image, ScrollView, FlatList',
                'Styling with StyleSheet and Flexbox layout',
                'Props and state with React hooks (useState, useEffect)'
              ]
            },
            Flutter: {
              stack: ['Flutter', 'Dart'],
              topics: [
                'Setting up a Flutter project and the widget tree',
                'Core widgets: Container, Text, Image, ListView, Column/Row',
                'Stateless vs. StatefulWidget',
                'Styling and layout with Flutter\'s widget-based system'
              ]
            }
          }
        },
        {
          period: 'Week 3',
          title: 'Navigation & State Management',
          scope: 'Turn a single screen into a multi-screen app with data flowing between them.',
          choice: true,
          variants: {
            'React Native': {
              stack: ['React Navigation', 'AsyncStorage'],
              topics: [
                'Stack and tab navigation with React Navigation',
                'Passing data between screens',
                'Local persistence with AsyncStorage',
                'Lifting state up vs. Context API for shared state'
              ]
            },
            Flutter: {
              stack: ['Navigator 2.0', 'Provider', 'shared_preferences'],
              topics: [
                'Screen navigation with Navigator and named routes',
                'Passing data between screens',
                'Local persistence with shared_preferences',
                'State management with Provider'
              ]
            }
          }
        },
        {
          period: 'Week 4',
          title: 'Consuming REST APIs & Device Permissions',
          scope: 'Connect the app to real data and request the device permissions a real app needs.',
          stack: ['React Native or Flutter', 'REST APIs', 'Device Permissions'],
          topics: [
            'Fetching and displaying data from a REST API',
            'Loading, error, and empty states on mobile',
            'Requesting and handling runtime permissions (camera, location)',
            'Handling network failures gracefully'
          ]
        },
        {
          period: 'Week 5',
          title: 'Testing & Debugging on Real Devices',
          scope: 'Move from the simulator to a real device and learn to debug what only shows up there.',
          stack: ['Android Studio', 'Xcode', 'Flipper/DevTools'],
          topics: [
            'Running and debugging on physical Android/iOS devices',
            'Using platform debugging tools (Flipper, React Native DevTools, Flutter DevTools)',
            'Common device-only bugs: keyboard overlap, safe areas, performance',
            'Basic widget/component testing'
          ]
        },
        {
          period: 'Week 6',
          title: 'Capstone: Build & Publish',
          scope: 'Plan, build, and package a complete mobile app end to end.',
          stack: ['React Native or Flutter', 'REST APIs', 'App Store/Play Store'],
          topics: [
            'Scoping a small app from a brief',
            'Building the app across the week with daily mentor check-ins',
            'Preparing a release build (icons, splash screen, app config)',
            'Demo day: presenting the finished app'
          ]
        }
      ],
      sixMonths: [
        {
          period: 'Month 1',
          title: 'Mobile Fundamentals',
          scope: 'Same foundation as the 6-week track — UI/UX and framework basics — covered at a deeper pace.',
          stack: ['Android/iOS guidelines', 'React Native or Flutter'],
          topics: [
            'Mobile UI/UX principles and platform guidelines',
            'Framework fundamentals: components/widgets, styling, layout',
            'Basic navigation between screens',
            'Weekly mini-projects with mentor code review'
          ]
        },
        {
          period: 'Month 2',
          title: 'Framework Engineering',
          scope: 'Go from framework basics to a production-style app structure with real navigation, state, and APIs.',
          choice: true,
          variants: {
            'React Native': {
              stack: ['React Native', 'React Navigation', 'Context API/Redux'],
              topics: [
                'Advanced navigation patterns (nested stacks, tabs, modals)',
                'App-wide state management (Context API or Redux)',
                'Consuming REST APIs with loading/error states',
                'Reusable component design'
              ]
            },
            Flutter: {
              stack: ['Flutter', 'Navigator 2.0', 'Provider/Riverpod'],
              topics: [
                'Advanced navigation patterns (nested routes, tabs, modals)',
                'App-wide state management with Provider or Riverpod',
                'Consuming REST APIs with loading/error states',
                'Reusable widget design'
              ]
            }
          }
        },
        {
          period: 'Month 3',
          title: 'Backend APIs for Mobile with ASP.NET Core',
          scope: 'Build the REST API your mobile app talks to, on the same .NET stack our engineering team ships in production.',
          stack: ['ASP.NET Core', 'C#', 'Entity Framework Core', 'SQL Server'],
          topics: [
            'Designing REST APIs for mobile clients (pagination, payload size, versioning)',
            'Building CRUD endpoints with ASP.NET Core Web API',
            'Data access with Entity Framework Core and SQL Server',
            'API documentation with Swagger/OpenAPI'
          ]
        },
        {
          period: 'Month 4',
          title: 'Authentication, Push Notifications & Cloud Storage',
          scope: 'Add the mobile-specific backend services almost every real app needs.',
          stack: ['Firebase Auth', 'Firebase Cloud Messaging', 'Firebase Storage', 'JWT'],
          topics: [
            'User authentication with Firebase Auth or JWT against your ASP.NET Core API',
            'Push notifications with Firebase Cloud Messaging',
            'Uploading and serving images/files with Firebase Storage',
            'Securing API endpoints so a user only sees their own data'
          ]
        },
        {
          period: 'Month 5',
          title: 'Performance, Testing & Release',
          scope: 'Take an app from "works on my phone" to a released, monitored app on both stores.',
          stack: ['Flutter Test/Jest & Detox', 'Fastlane', 'App Store Connect', 'Google Play Console'],
          topics: [
            'Performance profiling and reducing re-renders/rebuilds',
            'Automated testing for mobile (unit + widget/component tests)',
            'Build automation and release signing',
            'Submitting to the Google Play Console and App Store Connect'
          ]
        },
        {
          period: 'Month 6',
          title: 'Live Project & Capstone',
          scope: 'Build a complete mobile app end to end under mentor review, ready to present in interviews.',
          stack: ['React Native or Flutter', 'ASP.NET Core', 'Firebase'],
          topics: [
            'Project planning and architecture design',
            'Full app build across the month with weekly mentor reviews',
            'Backend + mobile integration, testing, and polish',
            'Final demo day and portfolio/resume preparation'
          ]
        }
      ]
    },
    curriculum: {
      sixWeeks: [
        'Mobile UI/UX fundamentals and platform guidelines (Android/iOS)',
        'Cross-platform development with React Native or Flutter (your choice)',
        'Navigation, state management, and local storage',
        'Consuming REST APIs from a mobile app',
        'Mini capstone: a working mobile app prototype'
      ],
      sixMonths: [
        'Everything in the 6-week track',
        'Push notifications, device APIs, and offline-first design',
        'Backend APIs with ASP.NET Core, authentication, and Firebase services',
        'App performance, testing, and debugging on real devices',
        'App store/Play Store packaging and release process',
        'Live project: a mobile app built and demoed under mentor review'
      ]
    },
    outcomes: [
      'Ship a cross-platform mobile app from scratch',
      'Understand mobile-specific UX, performance, and testing practices',
      'Hands-on experience with real device APIs and app deployment',
      'A portfolio-ready mobile project'
    ],
    tools: ['React Native', 'Flutter', 'ASP.NET Core', 'Firebase', 'REST APIs', 'Android Studio', 'Xcode'],
    seo: {
      title: 'Mobile App Development Training & Internship (React Native/Flutter) | EDSY Tech',
      description:
        'Mobile app development training & internship for BCA, B.Tech, MCA, M.Tech and B.Sc IT students. 6 weeks or 6 months, React Native or Flutter, ASP.NET Core backend. Apply now.',
      keywords:
        'mobile app development training, mobile app development internship, React Native training for students, Flutter training for students, mobile development training for BCA students, 6 weeks mobile training, 6 months mobile internship'
    },
    featured: true
  },
  {
    id: 3,
    slug: 'software-architecture-training',
    category: 'Architecture & Design',
    icon: 'Layers',
    title: 'Software Architecture & System Design Training',
    tagline: 'Learn how enterprise-grade systems are actually designed.',
    description:
      "A training track focused on how to design software systems that scale — architecture patterns, system design thinking, and the decisions that separate a student project from a production system.",
    scope:
      "Software architecture and system design is about the decisions that shape a system before a single feature ships: how it's broken into modules or services, how data flows and is stored, how it scales under load, and how it stays secure and maintainable for years. Trained architects and senior engineers make these calls; every engineer benefits from understanding them, since they explain why production systems look the way they do.",
    trainingGuideUrl: '/training-material/software-architecture-training-guide.md',
    trainingGuidePdfUrl: '/training-material/software-architecture-training-guide.pdf',
    durations: ['6 Weeks', '6 Months'],
    roadmap: {
      sixWeeks: [
        {
          period: 'Week 1',
          title: 'Software Design Principles',
          scope: 'Learn the principles that separate maintainable code from code that collapses under its own weight as it grows.',
          stack: ['SOLID', 'Design Patterns', 'C#'],
          topics: [
            'The SOLID principles with real before/after code examples',
            'Modularity and separation of concerns',
            'Common design patterns: Factory, Strategy, Repository, Observer',
            'When patterns help vs. when they over-engineer a simple problem'
          ]
        },
        {
          period: 'Week 2',
          title: 'Architecture Patterns',
          scope: 'Learn the major ways to structure a whole system, and the trade-offs each one makes.',
          stack: ['Layered Architecture', 'Microservices', 'Event-Driven Architecture'],
          topics: [
            'Layered (n-tier) architecture',
            'Microservices: benefits, costs, and when they\'re premature',
            'Event-driven architecture and message queues',
            'Monolith-first: why most systems shouldn\'t start as microservices'
          ]
        },
        {
          period: 'Week 3',
          title: 'API Design & Contract-First Thinking',
          scope: 'Design the contracts that let teams and services work independently without breaking each other.',
          stack: ['REST', 'OpenAPI/Swagger', 'Versioning'],
          topics: [
            'REST resource design and naming conventions',
            'Contract-first design with OpenAPI/Swagger',
            'API versioning strategies',
            'Designing for backward compatibility'
          ]
        },
        {
          period: 'Week 4',
          title: 'System Design Fundamentals',
          scope: 'Learn the vocabulary and trade-offs behind every "design a system like X" interview question and real architecture review.',
          stack: ['Load Balancing', 'Caching', 'CAP Theorem'],
          topics: [
            'Scalability: vertical vs. horizontal scaling',
            'Availability, redundancy, and failover',
            'The CAP theorem and what it actually means in practice',
            'Load balancers and caching layers at a high level'
          ]
        },
        {
          period: 'Week 5',
          title: 'Applying the Fundamentals',
          scope: 'Practice reading and critiquing real architecture, not just theory.',
          stack: ['Draw.io', 'Architecture Diagrams', 'Case Studies'],
          topics: [
            'Reading and producing architecture diagrams',
            'Case studies of real system designs and their trade-offs',
            'Identifying single points of failure in a design',
            'Estimating rough scale: requests/sec, data volume, storage'
          ]
        },
        {
          period: 'Week 6',
          title: 'Capstone: Design a System',
          scope: 'Take a business requirement and produce a complete architecture design, the way it happens before a real project starts.',
          stack: ['Architecture Decision Records', 'Diagrams'],
          topics: [
            'Translating a requirements brief into a system design',
            'Producing a diagram and a short Architecture Decision Record (ADR)',
            'Identifying trade-offs and documenting why you made each call',
            'Presenting the design to your mentor like a real design review'
          ]
        }
      ],
      sixMonths: [
        {
          period: 'Month 1',
          title: 'Design Principles & Patterns',
          scope: 'Same foundation as the 6-week track — SOLID and design patterns — covered at a deeper pace with more refactoring exercises.',
          stack: ['SOLID', 'Design Patterns', 'C#'],
          topics: [
            'SOLID principles with hands-on refactoring exercises',
            'Common design patterns and when to use each',
            'Code smells that signal a design principle is being violated',
            'Weekly review of real (anonymized) production code'
          ]
        },
        {
          period: 'Month 2',
          title: 'Architecture Patterns & API Design',
          scope: 'Go deep on structuring a whole system and the contracts between its parts.',
          stack: ['Microservices', 'Event-Driven Architecture', 'REST', 'OpenAPI'],
          topics: [
            'Layered, microservices, and event-driven architecture in depth',
            'Service boundaries and how to identify them',
            'Contract-first API design with OpenAPI/Swagger',
            'Designing for independent team ownership of services'
          ]
        },
        {
          period: 'Month 3',
          title: 'System Design & Scalability',
          scope: 'Learn how systems handle real-world scale and failure.',
          stack: ['Load Balancing', 'Caching (Redis)', 'Message Queues'],
          topics: [
            'Horizontal scaling, load balancing strategies',
            'Caching strategies and cache invalidation',
            'Message queues and asynchronous processing',
            'Designing for graceful degradation and failure'
          ]
        },
        {
          period: 'Month 4',
          title: 'Data Architecture',
          scope: 'Design how data is modeled, stored, and scaled — usually the hardest part to change later.',
          stack: ['SQL Server', 'NoSQL', 'Sharding', 'Multi-Tenancy'],
          topics: [
            'Relational vs. NoSQL trade-offs',
            'Database sharding and read replicas',
            'Multi-tenancy patterns for SaaS systems',
            'Data migration strategy for evolving schemas'
          ]
        },
        {
          period: 'Month 5',
          title: 'Security, Observability & Documentation',
          scope: 'Design the parts of a system that don\'t show up in a demo but matter most in production.',
          stack: ['Security Baselines', 'Observability', 'ADRs'],
          topics: [
            'Security architecture: authentication, authorization, data protection',
            'Observability: logging, metrics, and distributed tracing',
            'Writing Architecture Decision Records (ADRs)',
            'Producing board-ready architecture documentation'
          ]
        },
        {
          period: 'Month 6',
          title: 'Live Project & Capstone',
          scope: 'Architect a real system end to end under mentor review, the way it happens before a real project starts.',
          stack: ['Architecture Diagrams', 'ADRs', 'System Design'],
          topics: [
            'Requirements gathering and translating them into a design',
            'Full architecture design across the month with weekly mentor reviews',
            'Producing diagrams, ADRs, and a scaling/security plan',
            'Final presentation: defending your design like a real architecture review'
          ]
        }
      ]
    },
    curriculum: {
      sixWeeks: [
        'Software design principles: SOLID, modularity, separation of concerns',
        'Architecture patterns: layered, microservices, event-driven',
        'API design and contract-first thinking',
        'Introduction to system design: scalability, availability, trade-offs',
        'Mini exercise: designing the architecture for a sample application'
      ],
      sixMonths: [
        'Everything in the 6-week track',
        'Database and caching strategy for scale',
        'Designing for multi-tenancy, security, and observability',
        'Documenting architecture decisions (ADRs) and board-ready diagrams',
        'Case studies from real enterprise SaaS systems',
        'Live project: architecting and documenting a system design under mentor review'
      ]
    },
    outcomes: [
      'Read and produce architecture diagrams with confidence',
      'Understand the trade-offs behind common design patterns',
      'Practice translating business requirements into a system design',
      'A design portfolio piece for interviews'
    ],
    tools: ['System Design', 'Microservices', 'API Design', 'Draw.io', 'ADRs'],
    seo: {
      title: 'Software Architecture & System Design Training for Students | EDSY Tech',
      description:
        'Software architecture and system design training & internship for BCA, B.Tech, MCA and M.Tech students. 6 weeks or 6 months, enterprise architecture patterns and real case studies.',
      keywords:
        'software architecture training, system design training for students, architecture design internship, enterprise architecture training, system design internship'
    },
    featured: false
  },
  {
    id: 4,
    slug: 'seo-training',
    category: 'Marketing & Growth',
    icon: 'Search',
    title: 'SEO (Search Engine Optimization) Training & Internship',
    tagline: 'Learn how websites actually get found on Google.',
    description:
      "A practical SEO training and internship track covering on-page, technical, and content SEO — the same techniques we use to get our own websites and client sites ranking.",
    scope:
      "SEO (Search Engine Optimization) is the practice of making a website easier for search engines to crawl, understand, and rank well for the terms its audience actually searches — through on-page content and structure, technical site health, and off-page authority signals. Trained SEO practitioners work as SEO specialists, digital marketing executives, or content strategists, and the skill applies to every website or product a business ever ships.",
    trainingGuideUrl: '/training-material/seo-training-guide.md',
    trainingGuidePdfUrl: '/training-material/seo-training-guide.pdf',
    durations: ['6 Weeks', '6 Months'],
    roadmap: {
      sixWeeks: [
        {
          period: 'Week 1',
          title: 'How Search Engines Work & Keyword Research',
          scope: 'Understand what you\'re actually optimizing for before touching a single page.',
          stack: ['Google Search', 'Keyword Research Tools'],
          topics: [
            'How search engines crawl, index, and rank pages',
            'Search intent: informational, navigational, transactional',
            'Keyword research: finding what your audience actually searches',
            'Reading a SERP (search results page) like an SEO'
          ]
        },
        {
          period: 'Week 2',
          title: 'On-Page SEO',
          scope: 'Optimize the content and structure of an individual page for both users and search engines.',
          stack: ['Titles & Meta Descriptions', 'Heading Structure', 'Internal Linking'],
          topics: [
            'Title tags and meta descriptions that earn clicks',
            'Heading structure (H1-H3) and content hierarchy',
            'Internal linking strategy',
            'Writing content that satisfies search intent, not just keyword density'
          ]
        },
        {
          period: 'Week 3',
          title: 'Technical SEO Basics',
          scope: 'Make sure search engines can actually crawl and understand your site in the first place.',
          stack: ['Sitemaps', 'robots.txt', 'Page Speed', 'Mobile-Friendliness'],
          topics: [
            'XML sitemaps and robots.txt',
            'Crawlability and indexability basics',
            'Page speed fundamentals and Core Web Vitals (intro)',
            'Mobile-friendliness and responsive design as a ranking factor'
          ]
        },
        {
          period: 'Week 4',
          title: 'Search Console & Analytics',
          scope: 'Learn to measure what\'s actually happening instead of guessing.',
          stack: ['Google Search Console', 'Google Analytics'],
          topics: [
            'Setting up and reading Google Search Console',
            'Tracking impressions, clicks, and average position',
            'Google Analytics basics: traffic sources, landing pages, conversions',
            'Diagnosing a ranking or traffic drop'
          ]
        },
        {
          period: 'Week 5',
          title: 'Content Strategy & Off-Page Basics',
          scope: 'Understand what earns authority beyond your own pages.',
          stack: ['Content Strategy', 'Backlinks'],
          topics: [
            'Content strategy fundamentals: covering a topic, not just a keyword',
            'Introduction to off-page SEO and backlinks',
            'Competitor content gap analysis',
            'E-E-A-T basics (Experience, Expertise, Authoritativeness, Trust)'
          ]
        },
        {
          period: 'Week 6',
          title: 'Capstone: SEO Audit & Plan',
          scope: 'Produce the deliverable a real SEO consultant hands a client.',
          stack: ['SEO Audit', 'Search Console', 'Keyword Research'],
          topics: [
            'Auditing a real page across on-page, technical, and content dimensions',
            'Prioritizing fixes by expected impact vs. effort',
            'Producing a written SEO improvement plan',
            'Presenting findings and recommendations to your mentor'
          ]
        }
      ],
      sixMonths: [
        {
          period: 'Month 1',
          title: 'SEO Foundations',
          scope: 'Same foundation as the 6-week track — search fundamentals and on-page SEO — covered at a deeper pace.',
          stack: ['Keyword Research', 'On-Page SEO'],
          topics: [
            'How search engines crawl, index, and rank pages',
            'Keyword research and search intent in depth',
            'On-page SEO: titles, meta descriptions, headings, internal linking',
            'Weekly mini-audits with mentor review'
          ]
        },
        {
          period: 'Month 2',
          title: 'Technical SEO Deep Dive',
          scope: 'Go beyond the basics into the technical issues that quietly cap a site\'s ranking potential.',
          stack: ['Core Web Vitals', 'Schema.org', 'Crawl Budget'],
          topics: [
            'Core Web Vitals in depth: LCP, INP, CLS',
            'Structured data / schema markup for rich results',
            'Crawl budget and log file analysis basics',
            'SEO for JavaScript-rendered sites'
          ]
        },
        {
          period: 'Month 3',
          title: 'Content Strategy & Topic Clusters',
          scope: 'Plan content the way it drives rankings at scale, not one page at a time.',
          stack: ['Topic Clusters', 'Content Calendars'],
          topics: [
            'Topic clusters and pillar page strategy',
            'Content gap analysis against competitors',
            'Planning a content calendar around search intent',
            'Optimizing and refreshing existing content'
          ]
        },
        {
          period: 'Month 4',
          title: 'Off-Page SEO & Link Building',
          scope: 'Build the authority signals that on-page work alone can\'t create.',
          stack: ['Backlink Analysis', 'Outreach'],
          topics: [
            'Backlink profile analysis',
            'White-hat link building tactics and outreach',
            'Digital PR basics as an SEO tactic',
            'Recognizing and avoiding harmful/spammy link practices'
          ]
        },
        {
          period: 'Month 5',
          title: 'Local & E-Commerce SEO, Reporting',
          scope: 'Apply SEO to specific business models and learn to report results credibly.',
          stack: ['Google Business Profile', 'Local SEO', 'Analytics Reporting'],
          topics: [
            'Local SEO and Google Business Profile optimization',
            'SEO for multi-page/product/e-commerce sites',
            'Building an SEO performance report stakeholders actually read',
            'Setting and tracking realistic SEO KPIs'
          ]
        },
        {
          period: 'Month 6',
          title: 'Live Project & Capstone',
          scope: 'Execute and track a real SEO plan on a live site under mentor review.',
          stack: ['Search Console', 'Analytics', 'SEO Audit'],
          topics: [
            'Auditing and prioritizing a real site\'s SEO opportunities',
            'Executing on-page, technical, and content fixes across the month',
            'Tracking ranking/traffic changes with Search Console and Analytics',
            'Final report and presentation of results to your mentor'
          ]
        }
      ]
    },
    curriculum: {
      sixWeeks: [
        'How search engines crawl, index, and rank pages',
        'Keyword research and search intent',
        'On-page SEO: titles, meta descriptions, headings, internal linking',
        'Technical SEO basics: sitemaps, robots.txt, page speed, mobile-friendliness',
        'Introduction to Google Search Console & Analytics',
        'Mini project: an SEO audit and improvement plan for a real page'
      ],
      sixMonths: [
        'Everything in the 6-week track',
        'Structured data / schema markup for rich results',
        'Content strategy and topic clusters',
        'Off-page SEO and backlink fundamentals',
        'Local SEO and SEO for multi-page/product sites',
        'Live project: executing and tracking an SEO plan on a live site under mentor review'
      ]
    },
    outcomes: [
      'Perform a full SEO audit on a real website',
      'Understand how structured data and technical SEO affect rankings',
      'Hands-on experience with Search Console, Analytics, and keyword tools',
      'A completed SEO case study for your portfolio'
    ],
    tools: ['Google Search Console', 'Google Analytics', 'Keyword Research', 'Schema.org', 'Technical SEO'],
    seo: {
      title: 'SEO Training & Internship for Students | EDSY Tech',
      description:
        'SEO training & internship for BCA, B.Tech, MCA, M.Tech and B.Sc IT students. 6 weeks or 6 months, on-page, technical and content SEO with real projects. Apply now.',
      keywords:
        'SEO training, SEO internship, SEO training for students, digital marketing training, search engine optimization training, SEO training for BCA students'
    },
    featured: false
  },
  {
    id: 5,
    slug: 'server-administration-training',
    category: 'Infrastructure & Cloud',
    icon: 'Server',
    title: 'IT Infrastructure & Server Administration Training',
    tagline: 'Learn to configure, secure, and manage real servers.',
    description:
      "Hands-on training in Windows/Linux server administration and IT infrastructure management — the operational skills behind every reliable application.",
    scope:
      "IT infrastructure and server administration covers everything that keeps the machines behind an application running: installing and configuring servers, networking, user and access management, monitoring, backups, and security hardening. Trained infrastructure engineers work as system administrators, IT infrastructure engineers, or NOC/support engineers — the operational backbone every software team depends on.",
    trainingGuideUrl: '/training-material/server-administration-training-guide.md',
    trainingGuidePdfUrl: '/training-material/server-administration-training-guide.pdf',
    durations: ['6 Weeks', '6 Months'],
    roadmap: {
      sixWeeks: [
        {
          period: 'Week 1',
          title: 'Linux Server Fundamentals',
          scope: 'Get comfortable operating a server entirely from the command line — the default way most real servers are managed.',
          stack: ['Linux', 'Bash', 'SSH'],
          topics: [
            'The Linux filesystem hierarchy and core commands',
            'Users, groups, and file permissions (chmod/chown)',
            'Package management (apt/yum) and service management (systemd)',
            'Connecting to and administering a server over SSH'
          ]
        },
        {
          period: 'Week 2',
          title: 'Windows Server Fundamentals',
          scope: 'Learn the Windows side of infrastructure most enterprise environments still run on.',
          stack: ['Windows Server', 'Active Directory', 'PowerShell'],
          topics: [
            'Windows Server roles and features',
            'Active Directory basics: users, groups, organizational units',
            'Administering Windows Server with PowerShell',
            'Remote Desktop and remote server management'
          ]
        },
        {
          period: 'Week 3',
          title: 'Networking Basics',
          scope: 'Understand how servers actually talk to each other and to the internet.',
          stack: ['DNS', 'DHCP', 'Firewalls', 'Subnetting'],
          topics: [
            'IP addressing and subnetting fundamentals',
            'DNS: how name resolution works, common record types',
            'DHCP: how devices get network configuration automatically',
            'Firewalls and basic network security concepts'
          ]
        },
        {
          period: 'Week 4',
          title: 'User & Access Management',
          scope: 'Control who can do what on a server — the foundation of server security.',
          stack: ['SSH Keys', 'sudo', 'RBAC'],
          topics: [
            'SSH key-based authentication vs. passwords',
            'The principle of least privilege and sudo configuration',
            'Role-based access control (RBAC) concepts',
            'Auditing who has access to what'
          ]
        },
        {
          period: 'Week 5',
          title: 'Monitoring & Log Management',
          scope: 'Learn to see what a server is actually doing, before something breaks.',
          stack: ['syslog', 'Event Viewer', 'Monitoring Tools'],
          topics: [
            'Reading and interpreting Linux logs (syslog, journalctl)',
            'Windows Event Viewer basics',
            'Setting up basic uptime/resource monitoring and alerts',
            'Establishing a baseline so you can recognize abnormal behavior'
          ]
        },
        {
          period: 'Week 6',
          title: 'Capstone: Set Up & Secure a Server',
          scope: 'Provision and harden a server environment end to end, the way it happens before an application is deployed to it.',
          stack: ['Linux/Windows Server', 'Firewalls', 'SSH'],
          topics: [
            'Provisioning a server from scratch',
            'Applying a security baseline (firewall rules, key-based auth, updates)',
            'Setting up basic monitoring',
            'Documenting the setup and demoing it to your mentor'
          ]
        }
      ],
      sixMonths: [
        {
          period: 'Month 1',
          title: 'Linux & Windows Server Fundamentals',
          scope: 'Same foundation as the 6-week track — both operating systems — covered at a deeper pace.',
          stack: ['Linux', 'Windows Server', 'PowerShell', 'Bash'],
          topics: [
            'Linux fundamentals: filesystem, permissions, package/service management',
            'Windows Server fundamentals: roles, Active Directory basics',
            'Command-line administration on both platforms',
            'Weekly hands-on labs with mentor review'
          ]
        },
        {
          period: 'Month 2',
          title: 'Networking & Security',
          scope: 'Go deeper on how servers communicate and how to keep that communication secure.',
          stack: ['DNS', 'DHCP', 'Firewalls', 'VPN'],
          topics: [
            'Networking in depth: subnetting, routing basics, VLANs (intro)',
            'Firewall configuration (iptables/Windows Firewall)',
            'VPN basics for secure remote access',
            'Security hardening: disabling unused services, patch baselines'
          ]
        },
        {
          period: 'Month 3',
          title: 'Virtualization & Containerization',
          scope: 'Learn the technologies that let one physical server run many isolated environments.',
          stack: ['Hyper-V/VMware', 'Docker'],
          topics: [
            'Virtualization fundamentals: hypervisors, virtual machines',
            'Creating and managing VMs (Hyper-V or VMware)',
            'Containerization basics with Docker',
            'When to use a VM vs. a container'
          ]
        },
        {
          period: 'Month 4',
          title: 'Scripting & Automation',
          scope: 'Stop doing repetitive admin tasks by hand.',
          stack: ['PowerShell', 'Bash', 'Infrastructure as Code (intro)'],
          topics: [
            'Writing PowerShell scripts to automate Windows admin tasks',
            'Writing Bash scripts to automate Linux admin tasks',
            'Scheduling tasks (cron / Task Scheduler)',
            'Introduction to Infrastructure as Code concepts'
          ]
        },
        {
          period: 'Month 5',
          title: 'Backup, Disaster Recovery & Compliance',
          scope: 'Prepare for the day something goes wrong.',
          stack: ['Backup Strategy', 'DR Planning', 'Patch Management'],
          topics: [
            'Backup strategies: full/incremental, retention policies',
            'Disaster recovery planning and recovery time/point objectives',
            'Patch management processes',
            'Basic compliance and audit-readiness concepts'
          ]
        },
        {
          period: 'Month 6',
          title: 'Live Project & Capstone',
          scope: 'Manage a real infrastructure environment end to end under mentor review.',
          stack: ['Linux/Windows Server', 'Monitoring', 'Automation Scripts'],
          topics: [
            'Provisioning and configuring a multi-server environment',
            'Applying networking, security, and monitoring across the month',
            'Automating routine maintenance with scripts',
            'Final demo and documentation handoff'
          ]
        }
      ]
    },
    curriculum: {
      sixWeeks: [
        'Linux and Windows Server fundamentals',
        'Networking basics: DNS, DHCP, firewalls, load balancers',
        'User, permission, and access management',
        'Server monitoring and log management',
        'Mini project: setting up and securing a server environment'
      ],
      sixMonths: [
        'Everything in the 6-week track',
        'Virtualization and containerization fundamentals',
        'Backup, disaster recovery, and patch management',
        'Automating server tasks with scripting (PowerShell/Bash)',
        'Security hardening and compliance basics',
        'Live project: managing an infrastructure environment under mentor review'
      ]
    },
    outcomes: [
      'Configure and secure Windows/Linux servers independently',
      'Understand core networking and access-management concepts',
      'Automate routine admin tasks with scripts',
      'Practical experience relevant to sysadmin/infra roles'
    ],
    tools: ['Windows Server', 'Linux', 'Networking', 'PowerShell', 'Bash', 'Monitoring Tools'],
    seo: {
      title: 'IT Infrastructure & Server Administration Training for Students | EDSY Tech',
      description:
        'Server administration and IT infrastructure training & internship for BCA, B.Tech, MCA and M.Tech students. 6 weeks or 6 months, Windows/Linux, real server projects.',
      keywords:
        'server administration training, IT infrastructure training, server admin internship, Linux training for students, Windows Server training, infrastructure internship'
    },
    featured: false
  },
  {
    id: 6,
    slug: 'cloud-infrastructure-training',
    category: 'Infrastructure & Cloud',
    icon: 'Cloud',
    title: 'Cloud Infrastructure Training & Internship',
    tagline: 'Deploy and manage real workloads on Azure and AWS.',
    description:
      "A training and internship track covering cloud infrastructure fundamentals primarily on Microsoft Azure (with AWS equivalents throughout) — compute, storage, networking, and the DevOps practices used to ship reliably.",
    scope:
      "Cloud infrastructure covers designing, deploying, and operating applications on cloud platforms instead of on-premises servers: compute, storage, networking, identity, and the DevOps/automation practices that keep deployments reliable and repeatable. Trained cloud engineers work as cloud engineers, DevOps engineers, or site reliability engineers — roles in high demand as more infrastructure moves to the cloud.",
    trainingGuideUrl: '/training-material/cloud-infrastructure-training-guide.md',
    trainingGuidePdfUrl: '/training-material/cloud-infrastructure-training-guide.pdf',
    durations: ['6 Weeks', '6 Months'],
    roadmap: {
      sixWeeks: [
        {
          period: 'Week 1',
          title: 'Cloud Computing Fundamentals',
          scope: 'Understand the cloud service models and what you\'re actually responsible for vs. the provider.',
          stack: ['Azure', 'AWS', 'Cloud Concepts'],
          topics: [
            'IaaS vs. PaaS vs. SaaS',
            'The shared responsibility model',
            'Regions, availability zones, and resiliency basics',
            'Setting up an Azure free account and the Azure CLI'
          ]
        },
        {
          period: 'Week 2',
          title: 'Compute & Storage',
          scope: 'Provision the two most fundamental cloud resources: somewhere to run code, and somewhere to store data.',
          stack: ['Azure App Service', 'Azure VMs', 'Blob Storage'],
          topics: [
            'Virtual machines: sizing, provisioning, connecting',
            'Platform-as-a-Service compute (Azure App Service / AWS Elastic Beanstalk)',
            'Object storage (Azure Blob Storage / AWS S3)',
            'Choosing IaaS vs. PaaS for a given workload'
          ]
        },
        {
          period: 'Week 3',
          title: 'Networking & Identity',
          scope: 'Control who can reach your resources, and who can manage them.',
          stack: ['Virtual Networks', 'Network Security Groups', 'Azure AD/IAM'],
          topics: [
            'Virtual networks and subnets in the cloud',
            'Network security groups / security groups',
            'Cloud identity and access management (Azure AD / AWS IAM)',
            'The principle of least privilege applied to cloud roles'
          ]
        },
        {
          period: 'Week 4',
          title: 'Infrastructure as Code',
          scope: 'Stop clicking through a portal — define infrastructure in code you can version and repeat.',
          stack: ['Terraform', 'Bicep/ARM'],
          topics: [
            'Why Infrastructure as Code over manual provisioning',
            'Terraform basics: providers, resources, state',
            'Provisioning a VM and storage account with Terraform',
            'Introduction to Azure Bicep/ARM templates'
          ]
        },
        {
          period: 'Week 5',
          title: 'Deploying an Application to the Cloud',
          scope: 'Put a real application on the cloud infrastructure you\'ve provisioned.',
          stack: ['Azure App Service', 'Azure SQL Database'],
          topics: [
            'Deploying a web app to Azure App Service',
            'Connecting an app to a managed database (Azure SQL Database)',
            'Environment variables and configuration in the cloud',
            'Basic health checks and restart policies'
          ]
        },
        {
          period: 'Week 6',
          title: 'Capstone: Deploy & Present',
          scope: 'Provision, deploy, and document a cloud environment end to end.',
          stack: ['Azure', 'Terraform', 'App Service'],
          topics: [
            'Provisioning infrastructure with Terraform',
            'Deploying an application into that infrastructure',
            'Basic monitoring and cost awareness',
            'Demo day: presenting your deployed environment'
          ]
        }
      ],
      sixMonths: [
        {
          period: 'Month 1',
          title: 'Cloud Fundamentals',
          scope: 'Same foundation as the 6-week track — service models, compute, storage — covered at a deeper pace.',
          stack: ['Azure', 'AWS', 'Cloud Concepts'],
          topics: [
            'IaaS/PaaS/SaaS and the shared responsibility model',
            'Compute and storage services in depth',
            'Hands-on labs provisioning resources via CLI and portal',
            'Weekly mini-projects with mentor review'
          ]
        },
        {
          period: 'Month 2',
          title: 'Networking, Identity & Infrastructure as Code',
          scope: 'Go deep on securing and codifying cloud infrastructure.',
          stack: ['Virtual Networks', 'Azure AD/IAM', 'Terraform'],
          topics: [
            'Networking in depth: VNets, peering, private endpoints',
            'Identity and access management in depth: roles, policies, service principals',
            'Terraform modules and remote state',
            'Managing multiple environments (dev/staging/prod) as code'
          ]
        },
        {
          period: 'Month 3',
          title: 'CI/CD Pipelines',
          scope: 'Automate the path from a code commit to a running deployment.',
          stack: ['GitHub Actions', 'Azure DevOps', 'CI/CD'],
          topics: [
            'CI/CD concepts: build, test, deploy stages',
            'Building a pipeline with GitHub Actions or Azure DevOps',
            'Automated deployment to Azure App Service',
            'Deployment strategies: blue-green, rolling, canary (concepts)'
          ]
        },
        {
          period: 'Month 4',
          title: 'Containerization & Kubernetes',
          scope: 'Package and orchestrate applications the way modern cloud platforms run them at scale.',
          stack: ['Docker', 'Kubernetes', 'Azure Kubernetes Service'],
          topics: [
            'Containerizing an application with Docker',
            'Pushing images to a container registry',
            'Kubernetes fundamentals: pods, deployments, services',
            'Deploying a containerized app to Azure Kubernetes Service (AKS)'
          ]
        },
        {
          period: 'Month 5',
          title: 'Cost Optimization, Monitoring & Security',
          scope: 'Operate cloud infrastructure responsibly, not just deploy it once.',
          stack: ['Azure Monitor', 'Cost Management', 'Security Baselines'],
          topics: [
            'Cloud cost optimization: right-sizing, reserved instances, budgets/alerts',
            'Monitoring and alerting with Azure Monitor / CloudWatch',
            'Cloud security baselines and compliance basics',
            'Disaster recovery basics in a cloud context'
          ]
        },
        {
          period: 'Month 6',
          title: 'Live Project & Capstone',
          scope: 'Deploy and operate a real cloud-hosted application end to end under mentor review.',
          stack: ['Azure', 'Terraform', 'CI/CD', 'Kubernetes'],
          topics: [
            'Designing and provisioning the target infrastructure',
            'Building a CI/CD pipeline for automated deployment',
            'Operating the deployed app: monitoring, cost, incident response',
            'Final demo day and portfolio/resume preparation'
          ]
        }
      ]
    },
    curriculum: {
      sixWeeks: [
        'Cloud computing fundamentals: IaaS, PaaS, SaaS',
        'Core Azure/AWS services: compute, storage, networking',
        'Cloud identity and access management basics',
        'Introduction to Infrastructure as Code (Terraform/ARM)',
        'Mini project: deploying an application to the cloud'
      ],
      sixMonths: [
        'Everything in the 6-week track',
        'CI/CD pipelines and automated deployments',
        'Containerization with Docker and an intro to Kubernetes',
        'Cloud cost optimization and monitoring',
        'Cloud security baselines and disaster recovery basics',
        'Live project: deploying and operating a cloud-hosted application under mentor review'
      ]
    },
    outcomes: [
      'Deploy and manage applications on Azure/AWS',
      'Build a basic CI/CD pipeline from scratch',
      'Understand Infrastructure as Code and containerization',
      'Hands-on cloud project for your resume'
    ],
    tools: ['Azure', 'AWS', 'Docker', 'Kubernetes', 'Terraform', 'CI/CD'],
    seo: {
      title: 'Cloud Infrastructure Training & Internship (Azure/AWS) for Students | EDSY Tech',
      description:
        'Cloud infrastructure training & internship for BCA, B.Tech, MCA and M.Tech students. 6 weeks or 6 months, Azure & AWS, DevOps and CI/CD with real projects.',
      keywords:
        'cloud infrastructure training, cloud computing internship, Azure training for students, AWS training for students, DevOps training, cloud internship for BCA students'
    },
    featured: true
  },
  {
    id: 7,
    slug: 'ai-tools-training',
    category: 'AI & Emerging Tech',
    icon: 'Sparkles',
    title: 'AI Tools Training & Internship',
    tagline: 'Learn to use modern AI tools to build and ship faster.',
    description:
      "A practical training track on today's AI tooling landscape — coding assistants, AI-powered design and content tools, and how to integrate AI tools into a real development workflow.",
    scope:
      "AI tools training covers using today's AI-powered software effectively and responsibly in real work — coding assistants, content and research tools, and workflow automations built on AI APIs — plus the judgment to verify AI output rather than trust it blindly. This applies across almost every role now, from engineering to QA to operations.",
    trainingGuideUrl: '/training-material/ai-tools-training-guide.md',
    trainingGuidePdfUrl: '/training-material/ai-tools-training-guide.pdf',
    durations: ['6 Weeks', '6 Months'],
    roadmap: {
      sixWeeks: [
        {
          period: 'Week 1',
          title: 'The Modern AI Tools Landscape',
          scope: 'Map the categories of AI tools available today before diving into any one of them.',
          stack: ['AI Coding Assistants', 'AI Content Tools', 'AI Automation Tools'],
          topics: [
            'Categories: coding assistants, content/design tools, automation, data analysis',
            'How these tools differ from traditional software (probabilistic, not deterministic)',
            'Picking the right tool for a task vs. defaulting to one tool for everything',
            'Setting up accounts/access for the tools you\'ll use this program'
          ]
        },
        {
          period: 'Week 2',
          title: 'AI-Assisted Coding Workflows',
          scope: 'Use an AI coding assistant as a real part of your development workflow, not a novelty.',
          stack: ['Claude Code', 'GitHub Copilot'],
          topics: [
            'Code generation: writing functions, boilerplate, and tests with AI assistance',
            'Code review: using AI to review a diff before a human does',
            'Debugging with AI: pasting errors/stack traces for a diagnosis',
            'Where AI coding assistants help most vs. where they mislead'
          ]
        },
        {
          period: 'Week 3',
          title: 'AI for Research, Documentation & Testing',
          scope: 'Extend AI assistance beyond writing code into the surrounding work.',
          stack: ['AI Research Tools', 'AI Documentation Tools'],
          topics: [
            'Using AI to research an unfamiliar library or API quickly',
            'Drafting documentation (README, comments, API docs) with AI, then editing it',
            'Generating test cases with AI and reviewing them for real coverage',
            'Summarizing long documents/threads with AI'
          ]
        },
        {
          period: 'Week 4',
          title: 'Evaluating AI Output',
          scope: 'Build the verification habits that separate safe AI use from risky AI use.',
          stack: ['Verification Techniques'],
          topics: [
            'Recognizing hallucinations: confident-sounding but wrong output',
            'Verifying AI-generated code actually runs and does what it claims',
            'Fact-checking AI-generated claims against a primary source',
            'When to trust AI output directly vs. when to independently verify'
          ]
        },
        {
          period: 'Week 5',
          title: 'Connecting AI Tools to Workflows',
          scope: 'Move from using AI tools manually to wiring them into a repeatable workflow.',
          stack: ['AI APIs', 'Basic Automation'],
          topics: [
            'Introduction to calling an AI API programmatically',
            'Simple automations: AI-generated summaries, classifications, or drafts triggered by an event',
            'Rate limits, cost, and basic error handling when calling an AI API',
            'Deciding what should stay manual vs. what\'s worth automating'
          ]
        },
        {
          period: 'Week 6',
          title: 'Capstone: Ship with AI Assistance',
          scope: 'Ship a real, small feature using an AI-assisted workflow end to end.',
          stack: ['AI Coding Assistants', 'AI APIs'],
          topics: [
            'Scoping a small feature or tool to build',
            'Building it using AI assistance at every applicable stage',
            'Documenting where AI helped, where it didn\'t, and what you verified',
            'Demo day: presenting the feature and your workflow'
          ]
        }
      ],
      sixMonths: [
        {
          period: 'Month 1',
          title: 'AI Tools Landscape & Coding Workflows',
          scope: 'Same foundation as the 6-week track — the tools landscape and AI-assisted coding — covered at a deeper pace.',
          stack: ['AI Coding Assistants', 'AI Content Tools'],
          topics: [
            'Categories of AI tools and how to choose between them',
            'AI-assisted coding: generation, review, debugging',
            'Building the habit of AI-assisted work without losing code ownership',
            'Weekly practice tasks with mentor review'
          ]
        },
        {
          period: 'Month 2',
          title: 'Research, Documentation, Testing & Evaluation',
          scope: 'Deepen AI-assisted work beyond code, and build rigorous verification habits.',
          stack: ['AI Research Tools', 'Verification Techniques'],
          topics: [
            'AI-assisted research, documentation, and test generation in depth',
            'Systematic evaluation of AI output: accuracy, completeness, bias',
            'Building a personal checklist for verifying AI-generated work',
            'Case studies of AI mistakes and what would have caught them'
          ]
        },
        {
          period: 'Month 3',
          title: 'AI APIs & Automation Fundamentals',
          scope: 'Learn to call AI models programmatically and build simple automations around them.',
          stack: ['AI APIs', 'REST APIs', 'Automation'],
          topics: [
            'Calling an AI API from code: requests, responses, streaming basics',
            'Structuring inputs and parsing structured outputs',
            'Basic automations: triggered summaries, classifications, or drafts',
            'Cost and rate-limit awareness when building on AI APIs'
          ]
        },
        {
          period: 'Month 4',
          title: 'AI-Assisted QA, Data Analysis & Reporting',
          scope: 'Apply AI tools to quality assurance and turning raw data into insight.',
          stack: ['AI QA Tools', 'Data Analysis'],
          topics: [
            'AI-assisted test case generation and exploratory testing ideas',
            'Using AI to analyze and summarize datasets',
            'Generating reports and dashboards with AI assistance',
            'Validating AI-generated analysis against the raw data'
          ]
        },
        {
          period: 'Month 5',
          title: 'Responsible AI Use',
          scope: 'Understand the limitations and risks of AI tools well enough to use them safely at work.',
          stack: ['AI Safety', 'Bias & Limitations'],
          topics: [
            'Known limitations of current AI models: hallucination, recency, context limits',
            'Recognizing and mitigating bias in AI-generated content',
            'Data privacy considerations when using AI tools with sensitive information',
            'Building organizational habits (not just personal ones) around safe AI use'
          ]
        },
        {
          period: 'Month 6',
          title: 'Live Project & Capstone',
          scope: 'Build a real AI-assisted internal tool or workflow end to end under mentor review.',
          stack: ['AI APIs', 'Automation', 'AI Coding Assistants'],
          topics: [
            'Scoping an AI-assisted tool or workflow with real utility',
            'Building it across the month with weekly mentor reviews',
            'Documenting its limitations and the verification steps built in',
            'Final demo day and portfolio/resume preparation'
          ]
        }
      ]
    },
    curriculum: {
      sixWeeks: [
        'Landscape of modern AI tools: coding assistants, content, design, automation',
        'AI-assisted coding workflows (code generation, review, debugging)',
        'Using AI tools for research, documentation, and testing',
        'Evaluating AI tool output for accuracy and quality',
        'Mini project: shipping a small feature using an AI-assisted workflow'
      ],
      sixMonths: [
        'Everything in the 6-week track',
        'Connecting AI tools to real workflows via APIs and automations',
        'AI-assisted QA, data analysis, and reporting',
        'Responsible AI use: limitations, bias, and verification habits',
        'Building simple internal tools powered by AI APIs',
        'Live project: an AI-assisted internal tool or workflow under mentor review'
      ]
    },
    outcomes: [
      'Confidently use AI tools across the development lifecycle',
      'Understand how to verify and quality-check AI-generated output',
      'Build a small AI-powered tool or workflow automation',
      'A practical, resume-ready AI tooling project'
    ],
    tools: ['AI Coding Assistants', 'AI APIs', 'Automation Tools', 'Prompting Basics'],
    seo: {
      title: 'AI Tools Training & Internship for Students | EDSY Tech',
      description:
        'AI tools training & internship for BCA, B.Tech, MCA, M.Tech and B.Sc IT students. 6 weeks or 6 months, hands-on with modern AI coding and productivity tools.',
      keywords:
        'AI tools training, AI training for students, AI internship, AI tools training for BCA students, artificial intelligence training, AI productivity tools training'
    },
    featured: true
  },
  {
    id: 8,
    slug: 'prompt-engineering-training',
    category: 'AI & Emerging Tech',
    icon: 'MessageSquare',
    title: 'Prompt Engineering Training & Internship',
    tagline: 'Learn to design prompts that get reliable, high-quality results from AI models.',
    description:
      "A focused training track on prompt engineering — how to design, test, and refine prompts for large language models to get consistent, production-quality output.",
    scope:
      "Prompt engineering is the practice of designing inputs to large language models that reliably produce the output you want — clear instructions, the right context, and techniques like few-shot examples and structured output formats. It's a skill that compounds across every other AI-related track, since every AI tool, coding assistant, and Claude-powered application is ultimately driven by prompts.",
    trainingGuideUrl: '/training-material/prompt-engineering-training-guide.md',
    trainingGuidePdfUrl: '/training-material/prompt-engineering-training-guide.pdf',
    durations: ['6 Weeks', '6 Months'],
    roadmap: {
      sixWeeks: [
        {
          period: 'Week 1',
          title: 'How LLMs Process Prompts',
          scope: 'Understand what a model is actually doing with your prompt before trying to optimize it.',
          stack: ['Tokens', 'Context Window', 'LLM Basics'],
          topics: [
            'Tokens: how text is broken down and why it matters for prompting',
            'The context window and its limits',
            'Next-token prediction: why models can be confidently wrong',
            'How training data and instructions shape model behavior'
          ]
        },
        {
          period: 'Week 2',
          title: 'Prompt Design Fundamentals',
          scope: 'Learn the core levers that separate a vague prompt from a reliable one.',
          stack: ['System Prompts', 'Prompt Structure'],
          topics: [
            'Clarity: specific instructions over vague requests',
            'Context: giving the model what it needs to know',
            'Constraints: format, length, tone, and scope boundaries',
            'System prompts vs. user prompts'
          ]
        },
        {
          period: 'Week 3',
          title: 'Few-Shot Prompting & Chain-of-Thought',
          scope: 'Use examples and reasoning structure to dramatically improve output quality.',
          stack: ['Few-Shot Examples', 'Chain-of-Thought'],
          topics: [
            'Zero-shot vs. few-shot prompting',
            'Choosing and formatting examples that actually teach the pattern',
            'Chain-of-thought prompting for multi-step reasoning tasks',
            'When more examples help vs. when they add noise'
          ]
        },
        {
          period: 'Week 4',
          title: 'Structured Outputs',
          scope: 'Get AI output your code can actually rely on, not just prose to read.',
          stack: ['JSON Output', 'Output Formatting'],
          topics: [
            'Requesting structured formats (JSON, tables, specific templates)',
            'Defining a schema the model should follow',
            'Handling malformed output gracefully',
            'Trade-offs between structured output and free-form quality'
          ]
        },
        {
          period: 'Week 5',
          title: 'Testing, Iteration & Evaluation',
          scope: 'Treat prompt design as an iterative, testable process, not a one-shot guess.',
          stack: ['Prompt Testing', 'Evaluation Techniques'],
          topics: [
            'Building a small test set of representative inputs',
            'Comparing prompt variants systematically',
            'Common failure modes and how to prompt around them',
            'Knowing when a prompt is "good enough" to ship'
          ]
        },
        {
          period: 'Week 6',
          title: 'Capstone: Build a Prompt Library',
          scope: 'Produce a reusable, tested set of prompts for a real use case.',
          stack: ['Prompt Library', 'Documentation'],
          topics: [
            'Choosing a real use case (e.g. support replies, content drafts, code review)',
            'Designing and testing 5-8 prompts covering that use case',
            'Documenting each prompt\'s purpose, expected input, and output format',
            'Demo day: presenting the library and test results'
          ]
        }
      ],
      sixMonths: [
        {
          period: 'Month 1',
          title: 'LLM Fundamentals & Prompt Design',
          scope: 'Same foundation as the 6-week track — how LLMs work and core prompt design — covered at a deeper pace.',
          stack: ['Tokens', 'System Prompts', 'Prompt Structure'],
          topics: [
            'How LLMs process prompts and generate output',
            'Prompt design fundamentals: clarity, context, constraints',
            'System vs. user prompts in depth',
            'Weekly prompt-writing exercises with mentor review'
          ]
        },
        {
          period: 'Month 2',
          title: 'Few-Shot, Chain-of-Thought & Structured Outputs',
          scope: 'Master the techniques that most reliably improve output quality and consistency.',
          stack: ['Few-Shot Examples', 'Chain-of-Thought', 'JSON Output'],
          topics: [
            'Few-shot prompting in depth: example selection and formatting',
            'Chain-of-thought and step-by-step reasoning prompts',
            'Structured output design with schemas',
            'Combining techniques for complex, multi-part tasks'
          ]
        },
        {
          period: 'Month 3',
          title: 'System Prompts & Multi-Turn Conversation Design',
          scope: 'Design prompts for ongoing conversations, not just single requests.',
          stack: ['System Prompts', 'Conversation Design'],
          topics: [
            'Designing effective system prompts for an application\'s persona/role',
            'Managing conversation history and context across turns',
            'Handling topic drift and staying on-task across a conversation',
            'Designing for conversations that need to remember prior context'
          ]
        },
        {
          period: 'Month 4',
          title: 'Tool Use & Agentic Prompt Patterns',
          scope: 'Design prompts for models that can call tools/functions to take action, not just respond.',
          stack: ['Tool Use / Function Calling', 'Agentic Patterns'],
          topics: [
            'Function calling / tool use basics',
            'Designing tool descriptions the model can use reliably',
            'Multi-step agentic patterns: plan, act, observe, repeat',
            'Common failure modes in agentic prompting and how to prompt around them'
          ]
        },
        {
          period: 'Month 5',
          title: 'Prompt Safety & Guardrails',
          scope: 'Design prompts that handle model limitations and misuse gracefully.',
          stack: ['Prompt Safety', 'Guardrails'],
          topics: [
            'Handling ambiguous or malicious user input',
            'Guardrail patterns: constraining scope, refusing gracefully',
            'Known model limitations and prompting around them',
            'Testing prompts against edge cases and adversarial input'
          ]
        },
        {
          period: 'Month 6',
          title: 'Live Project & Capstone',
          scope: 'Build a real prompt-driven feature or assistant, integrated via API, under mentor review.',
          stack: ['LLM APIs', 'Prompt Library', 'Structured Outputs'],
          topics: [
            'Scoping a prompt-driven feature or assistant with real utility',
            'Building and integrating it via an LLM API across the month',
            'Testing and refining prompts against real usage',
            'Final demo day and portfolio/resume preparation'
          ]
        }
      ]
    },
    curriculum: {
      sixWeeks: [
        'How large language models process and respond to prompts',
        'Prompt design fundamentals: clarity, context, constraints',
        'Few-shot prompting, chain-of-thought, and structured outputs',
        'Prompt testing, iteration, and evaluation',
        'Mini project: a prompt library for a real use case'
      ],
      sixMonths: [
        'Everything in the 6-week track',
        'System prompts and multi-turn conversation design',
        'Tool use / function calling and agentic prompt patterns',
        'Prompt safety, guardrails, and handling model limitations',
        'Integrating prompts into applications via APIs',
        'Live project: a prompt-driven feature or assistant under mentor review'
      ]
    },
    outcomes: [
      'Design and iterate on prompts systematically, not by guesswork',
      'Understand few-shot, chain-of-thought, and structured-output techniques',
      'Build a small prompt-driven application or internal tool',
      'A portfolio-ready prompt engineering project'
    ],
    tools: ['LLM APIs', 'Prompt Design', 'Structured Outputs', 'Evaluation Techniques'],
    seo: {
      title: 'Prompt Engineering Training & Internship for Students | EDSY Tech',
      description:
        'Prompt engineering training & internship for BCA, B.Tech, MCA, M.Tech and B.Sc IT students. 6 weeks or 6 months, hands-on LLM prompting and real projects.',
      keywords:
        'prompt engineering training, prompt engineering internship, prompt engineering course for students, LLM training for students, AI prompt training'
    },
    featured: true
  },
  {
    id: 9,
    slug: 'claude-ai-training',
    category: 'AI & Emerging Tech',
    icon: 'Bot',
    title: 'Claude (Anthropic) AI Training & Internship',
    tagline: 'Learn to build with Claude — Anthropic’s AI models and developer tools.',
    description:
      "A dedicated training track on building with Claude, Anthropic's family of AI models — the Claude API, tool use, and Claude-powered developer workflows like Claude Code.",
    scope:
      "This track is a deep, hands-on specialization in Claude — Anthropic's family of AI models — covering the Claude API, prompting techniques Claude responds to best, Claude Code for AI-assisted development, and building real applications and agents on top of Claude. It complements the broader Prompt Engineering and AI Tools tracks with model-specific depth.",
    trainingGuideUrl: '/training-material/claude-ai-training-guide.md',
    trainingGuidePdfUrl: '/training-material/claude-ai-training-guide.pdf',
    durations: ['6 Weeks', '6 Months'],
    roadmap: {
      sixWeeks: [
        {
          period: 'Week 1',
          title: 'Introduction to Claude & Anthropic\'s Model Family',
          scope: 'Understand what Claude is, how the model family is organized, and when to use which model.',
          stack: ['Claude', 'Anthropic'],
          topics: [
            'Anthropic\'s model family: Opus, Sonnet, and Haiku, and their trade-offs',
            'What Claude is designed for: reasoning, coding, long-context tasks',
            'Setting up an Anthropic API account and API keys',
            'Claude.ai vs. the Claude API vs. Claude Code — when to use each'
          ]
        },
        {
          period: 'Week 2',
          title: 'Claude API Fundamentals',
          scope: 'Make your first API calls and understand the building blocks of every Claude integration.',
          stack: ['Claude API', 'Anthropic SDK'],
          topics: [
            'The Messages API: roles, turns, and message structure',
            'System prompts for setting persistent behavior',
            'Streaming responses for a responsive UI',
            'Handling API errors and rate limits'
          ]
        },
        {
          period: 'Week 3',
          title: 'Prompting Best Practices for Claude',
          scope: 'Apply prompting techniques that work especially well with Claude specifically.',
          stack: ['XML Tags', 'Extended Thinking', 'Prompt Design'],
          topics: [
            'Using XML tags to structure complex prompts for Claude',
            'Giving Claude a clear role and explicit instructions',
            'Working with Claude\'s long context window effectively',
            'When and how to use extended thinking for hard reasoning tasks'
          ]
        },
        {
          period: 'Week 4',
          title: 'Claude Code & Claude-Powered Developer Workflows',
          scope: 'Use Claude as a real part of your development workflow, not just a chat window.',
          stack: ['Claude Code', 'CLI Workflows'],
          topics: [
            'Getting started with Claude Code in a real project',
            'Using Claude Code for reading, editing, and refactoring code',
            'Reviewing and directing AI-driven changes safely',
            'Claude Code vs. chat-based coding assistance: when each fits better'
          ]
        },
        {
          period: 'Week 5',
          title: 'Building a Claude-Powered Application',
          scope: 'Wire the Claude API into a real, working application.',
          stack: ['Claude API', 'REST APIs'],
          topics: [
            'Designing an application feature around Claude',
            'Structured outputs from Claude for reliable app integration',
            'Managing conversation state across a session',
            'Basic cost and latency considerations when calling the API'
          ]
        },
        {
          period: 'Week 6',
          title: 'Capstone: Ship a Claude-Powered App',
          scope: 'Build and demo a small, complete application powered by the Claude API.',
          stack: ['Claude API', 'Claude Code'],
          topics: [
            'Scoping a small Claude-powered app from a brief',
            'Building it across the week, using Claude Code where useful',
            'Testing the app against varied real inputs',
            'Demo day: presenting the finished app'
          ]
        }
      ],
      sixMonths: [
        {
          period: 'Month 1',
          title: 'Claude Fundamentals & API',
          scope: 'Same foundation as the 6-week track — the model family and API basics — covered at a deeper pace.',
          stack: ['Claude', 'Claude API', 'Anthropic SDK'],
          topics: [
            'Anthropic\'s model family and how to choose between models',
            'The Messages API, system prompts, and streaming in depth',
            'Error handling, retries, and rate limits',
            'Weekly hands-on API exercises with mentor review'
          ]
        },
        {
          period: 'Month 2',
          title: 'Advanced Prompting & Claude Code',
          scope: 'Go deep on prompting techniques for Claude and AI-assisted development with Claude Code.',
          stack: ['Claude Code', 'XML Tags', 'Extended Thinking'],
          topics: [
            'Advanced prompt structuring with XML tags for complex tasks',
            'Extended thinking for hard multi-step reasoning problems',
            'Claude Code workflows: multi-file changes, planning mode, review habits',
            'Building a personal library of effective Claude prompts'
          ]
        },
        {
          period: 'Month 3',
          title: 'Tool Use & Agentic Workflows with Claude',
          scope: 'Let Claude call tools to take real action, not just respond with text.',
          stack: ['Tool Use / Function Calling', 'Agentic Patterns'],
          topics: [
            'Defining tools/functions for Claude to call',
            'Handling tool_use and tool_result turns in the Messages API',
            'Designing multi-step agent loops: plan, act, observe',
            'Debugging and constraining agentic behavior'
          ]
        },
        {
          period: 'Month 4',
          title: 'Retrieval-Augmented Workflows',
          scope: 'Connect Claude to real, current data instead of relying only on what it was trained on.',
          stack: ['RAG', 'Embeddings', 'Vector Search'],
          topics: [
            'Why retrieval-augmented generation (RAG) matters for real applications',
            'Embeddings and vector search fundamentals',
            'Building a simple retrieval pipeline feeding context to Claude',
            'Evaluating whether retrieved context actually improved the answer'
          ]
        },
        {
          period: 'Month 5',
          title: 'Multi-Step Agents, Automations & Responsible Use',
          scope: 'Build genuinely useful automations with Claude while using AI responsibly in production.',
          stack: ['Agentic Workflows', 'Automation', 'AI Safety'],
          topics: [
            'Building a multi-step automation powered by Claude and tool use',
            'Designing guardrails for an agent operating with real permissions',
            'Responsible AI use: verification habits, data privacy, human oversight',
            'Monitoring and logging AI-driven actions in production'
          ]
        },
        {
          period: 'Month 6',
          title: 'Live Project & Capstone',
          scope: 'Build a real Claude-powered feature or internal tool end to end under mentor review.',
          stack: ['Claude API', 'Claude Code', 'Tool Use'],
          topics: [
            'Scoping a Claude-powered feature or tool with real utility',
            'Building it across the month with weekly mentor reviews',
            'Testing, guardrails, and responsible-use documentation',
            'Final demo day and portfolio/resume preparation'
          ]
        }
      ]
    },
    curriculum: {
      sixWeeks: [
        'Introduction to Claude and Anthropic’s model family',
        'Working with the Claude API: messages, system prompts, streaming',
        'Prompting best practices specific to Claude',
        'Using Claude Code and Claude-powered developer workflows',
        'Mini project: a small application powered by the Claude API'
      ],
      sixMonths: [
        'Everything in the 6-week track',
        'Tool use / function calling with Claude for agentic workflows',
        'Retrieval-augmented workflows and connecting Claude to real data',
        'Building multi-step agents and automations with Claude',
        'Responsible and safe use of AI in production workflows',
        'Live project: a Claude-powered feature or internal tool under mentor review'
      ]
    },
    outcomes: [
      'Build applications on top of the Claude API with confidence',
      'Understand tool use and agentic patterns specific to Claude',
      'Practical experience with Claude Code and AI-assisted development',
      'A Claude-powered project for your resume and portfolio'
    ],
    tools: ['Claude API', 'Claude Code', 'Anthropic SDK', 'Tool Use / Function Calling'],
    seo: {
      title: 'Claude (Anthropic) AI Training & Internship for Students | EDSY Tech',
      description:
        'Claude (Anthropic) AI training & internship for BCA, B.Tech, MCA, M.Tech and B.Sc IT students. 6 weeks or 6 months, hands-on with the Claude API and Claude Code.',
      keywords:
        'Claude AI training, Anthropic training for students, Claude API training, Claude Code training, Claude internship, Anthropic Claude course for students'
    },
    featured: true
  }
];

export const getProgramBySlug = (slug) => {
  return trainingPrograms.find((program) => program.slug === slug);
};

export const getAllCategories = () => {
  return [...new Set(trainingPrograms.map((program) => program.category))];
};

export const getProgramsByCategory = (category) => {
  return trainingPrograms.filter((program) => program.category === category);
};

export const getRelatedPrograms = (slug, limit = 3) => {
  const current = getProgramBySlug(slug);
  if (!current) return [];
  return trainingPrograms
    .filter((program) => program.slug !== slug && program.category === current.category)
    .concat(trainingPrograms.filter((program) => program.slug !== slug && program.category !== current.category))
    .slice(0, limit);
};

export const getFeaturedPrograms = () => {
  return trainingPrograms.filter((program) => program.featured);
};
